'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { TrendingUp, Users, DollarSign, BarChart3 } from 'lucide-react';

interface Plan {
  name: string;
}

interface PlanOverviewSectionProps {
  plan: Plan;
}

export function PlanOverviewSection({ plan }: PlanOverviewSectionProps) {
  const t = useTranslations('planDetails.australianBinaryPlan.overview');

  const stats = [
    {
      icon: TrendingUp,
      label: t('stats.growthRate.label'),
      value: t('stats.growthRate.value'),
      description: t('stats.growthRate.description'),
    },
    {
      icon: Users,
      label: t('stats.teamBuilding.label'),
      value: t('stats.teamBuilding.value'),
      description: t('stats.teamBuilding.description'),
    },
    {
      icon: DollarSign,
      label: t('stats.earnings.label'),
      value: t('stats.earnings.value'),
      description: t('stats.earnings.description'),
    },
    {
      icon: BarChart3,
      label: t('stats.complexity.label'),
      value: t('stats.complexity.value'),
      description: t('stats.complexity.description'),
    },
  ];

  return (
    <SectionWrapper variant="default" padding="md" className="relative">
      <div className="container">
        <div className="mb-12">
          <Typography variant="h2" className="mb-4">
            {t('title')}
          </Typography>
          <Typography variant="p" className="text-gray-600 dark:text-gray-400 max-w-5xl">
            {t('description')}
          </Typography>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="bg-gradient-to-br from-primary-50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl p-6 border-2 border-primary-200 dark:border-primary-800 hover:border-primary-400 dark:hover:border-primary-600 transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <Typography variant="p" className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</Typography>
                    <Typography variant="p" className="text-xl font-bold text-gray-900 dark:text-white">{stat.value}</Typography>
                  </div>
                </div>
                <Typography variant="p" className="text-sm text-gray-600 dark:text-gray-400">{stat.description}</Typography>
              </div>
            );
          })}
        </div>
      </div>
    </SectionWrapper>
  );
}

